import type { DialogueLog } from '@shared/types/dialogue-log';
import {
  ResponseMappingError,
  reqArray,
  reqRecord,
} from '@shared/value-objects/parse-helpers';
import type {
  ContactLog,
  ContactLogSource,
  ContactLogType,
  ContactLogTypeCounts,
  ContractEvent,
  DealEvent,
  SourceRecord,
  TimelineCounts,
  TimelineItem,
  TimelineLawyerRef,
  TimelineResponse,
  TimelineSolutionRef,
} from './model';

/// API の生レスポンス (`unknown`) を検証して `TimelineResponse` にする門番 (parse, don't validate / #471)。
/// entry / contact_log / 商談 (deal_event) / 契約 (contract_event) はいずれも
/// branded 化対象外のため構造ガードのみで透過する (商談/契約イベントは backend が
/// 完全に投影した読み取り専用ペイロードで、domain エンティティのラウンドトリップを
/// 経由しない)。

function reqString(raw: unknown, field: string): string {
  if (typeof raw !== 'string') {
    throw new ResponseMappingError(
      field,
      `expected string, got ${raw === null ? 'null' : typeof raw}`,
    );
  }
  return raw;
}

function nullableString(raw: unknown, field: string): string | null {
  if (raw === null || raw === undefined) return null;
  return reqString(raw, field);
}

function reqBoolean(raw: unknown, field: string): boolean {
  if (typeof raw !== 'boolean') {
    throw new ResponseMappingError(
      field,
      `expected boolean, got ${raw === null ? 'null' : typeof raw}`,
    );
  }
  return raw;
}

function reqNumber(raw: unknown, field: string): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) {
    throw new ResponseMappingError(
      field,
      `expected number, got ${raw === null ? 'null' : typeof raw}`,
    );
  }
  return raw;
}

function parseContactLog(raw: unknown): ContactLog {
  const o = reqRecord(raw, 'timeline.item.contact_log');
  return {
    id: reqString(o.id, 'timeline.item.contact_log.id'),
    firm_id: nullableString(o.firm_id, 'timeline.item.contact_log.firm_id'),
    lawyer_id: nullableString(
      o.lawyer_id,
      'timeline.item.contact_log.lawyer_id',
    ),
    occurred_at: reqString(
      o.occurred_at,
      'timeline.item.contact_log.occurred_at',
    ),
    contact_log_type: reqString(
      o.contact_log_type,
      'timeline.item.contact_log.contact_log_type',
    ) as ContactLogType,
    behavior_type: reqString(
      o.behavior_type,
      'timeline.item.contact_log.behavior_type',
    ),
    source: reqString(
      o.source,
      'timeline.item.contact_log.source',
    ) as ContactLogSource,
    subject: nullableString(o.subject, 'timeline.item.contact_log.subject'),
    created_at: reqString(o.created_at, 'timeline.item.contact_log.created_at'),
    updated_at: reqString(o.updated_at, 'timeline.item.contact_log.updated_at'),
  };
}

function parseSourceRecord(raw: unknown, field: string): SourceRecord {
  const o = reqRecord(raw, field);
  return {
    kind: reqString(o.kind, `${field}.kind`),
    id: reqString(o.id, `${field}.id`),
    display_name: nullableString(o.display_name, `${field}.display_name`),
    can_navigate: reqBoolean(o.can_navigate, `${field}.can_navigate`),
  };
}

function parseLawyerRef(raw: unknown, field: string): TimelineLawyerRef {
  const o = reqRecord(raw, field);
  return {
    lawyer_id: reqString(o.lawyer_id, `${field}.lawyer_id`),
    display_name: nullableString(o.display_name, `${field}.display_name`),
  };
}

function parseSolutionRef(raw: unknown, field: string): TimelineSolutionRef {
  const o = reqRecord(raw, field);
  return {
    product_id: reqString(o.product_id, `${field}.product_id`),
    display_name: nullableString(o.display_name, `${field}.display_name`),
  };
}

function parseDealEvent(raw: unknown): DealEvent {
  const o = reqRecord(raw, 'timeline.item.deal_event');
  const targetLawyers = reqArray(
    o.target_lawyers,
    'timeline.item.deal_event.target_lawyers',
  ).map((l, i) => parseLawyerRef(l, `timeline.item.deal_event.target_lawyers[${i}]`));
  return {
    event_id: reqString(o.event_id, 'timeline.item.deal_event.event_id'),
    firm_id: reqString(o.firm_id, 'timeline.item.deal_event.firm_id'),
    anchor_lawyer_id: nullableString(
      o.anchor_lawyer_id,
      'timeline.item.deal_event.anchor_lawyer_id',
    ),
    from_sales_status: reqString(
      o.from_sales_status,
      'timeline.item.deal_event.from_sales_status',
    ),
    to_sales_status: reqString(
      o.to_sales_status,
      'timeline.item.deal_event.to_sales_status',
    ),
    from_action_status: reqString(
      o.from_action_status,
      'timeline.item.deal_event.from_action_status',
    ),
    to_action_status: reqString(
      o.to_action_status,
      'timeline.item.deal_event.to_action_status',
    ),
    actor_account_id: nullableString(
      o.actor_account_id,
      'timeline.item.deal_event.actor_account_id',
    ),
    product_id: nullableString(o.product_id, 'timeline.item.deal_event.product_id'),
    product_name: nullableString(
      o.product_name,
      'timeline.item.deal_event.product_name',
    ),
    lost_reason: nullableString(o.lost_reason, 'timeline.item.deal_event.lost_reason'),
    lost_reason_note: nullableString(
      o.lost_reason_note,
      'timeline.item.deal_event.lost_reason_note',
    ),
    target_lawyers: targetLawyers,
    recorded_at: reqString(o.recorded_at, 'timeline.item.deal_event.recorded_at'),
    source_record: parseSourceRecord(
      o.source_record,
      'timeline.item.deal_event.source_record',
    ),
  };
}

function parseContractEvent(raw: unknown): ContractEvent {
  const o = reqRecord(raw, 'timeline.item.contract_event');
  const solutions = reqArray(
    o.solutions,
    'timeline.item.contract_event.solutions',
  ).map((s, i) => parseSolutionRef(s, `timeline.item.contract_event.solutions[${i}]`));
  const targetLawyers = reqArray(
    o.target_lawyers,
    'timeline.item.contract_event.target_lawyers',
  ).map((l, i) =>
    parseLawyerRef(l, `timeline.item.contract_event.target_lawyers[${i}]`),
  );
  return {
    event_id: reqString(o.event_id, 'timeline.item.contract_event.event_id'),
    contract_id: reqString(o.contract_id, 'timeline.item.contract_event.contract_id'),
    deal_name: nullableString(o.deal_name, 'timeline.item.contract_event.deal_name'),
    contract_type: nullableString(
      o.contract_type,
      'timeline.item.contract_event.contract_type',
    ),
    is_individual_contract: reqBoolean(
      o.is_individual_contract,
      'timeline.item.contract_event.is_individual_contract',
    ),
    auto_renewal: reqBoolean(
      o.auto_renewal,
      'timeline.item.contract_event.auto_renewal',
    ),
    pipeline: nullableString(o.pipeline, 'timeline.item.contract_event.pipeline'),
    event_time_status: nullableString(
      o.event_time_status,
      'timeline.item.contract_event.event_time_status',
    ),
    current_status: nullableString(
      o.current_status,
      'timeline.item.contract_event.current_status',
    ),
    occurred_on: reqString(o.occurred_on, 'timeline.item.contract_event.occurred_on'),
    period_starts_on: nullableString(
      o.period_starts_on,
      'timeline.item.contract_event.period_starts_on',
    ),
    period_ends_on: nullableString(
      o.period_ends_on,
      'timeline.item.contract_event.period_ends_on',
    ),
    monthly_amount: nullableString(
      o.monthly_amount,
      'timeline.item.contract_event.monthly_amount',
    ),
    previous_monthly_amount: nullableString(
      o.previous_monthly_amount,
      'timeline.item.contract_event.previous_monthly_amount',
    ),
    renewal_method: nullableString(
      o.renewal_method,
      'timeline.item.contract_event.renewal_method',
    ),
    confirmation_route: nullableString(
      o.confirmation_route,
      'timeline.item.contract_event.confirmation_route',
    ),
    contract_party_id: nullableString(
      o.contract_party_id,
      'timeline.item.contract_event.contract_party_id',
    ),
    solutions,
    target_lawyers: targetLawyers,
    actor_account_id: nullableString(
      o.actor_account_id,
      'timeline.item.contract_event.actor_account_id',
    ),
    recorded_at: reqString(o.recorded_at, 'timeline.item.contract_event.recorded_at'),
    source_record: parseSourceRecord(
      o.source_record,
      'timeline.item.contract_event.source_record',
    ),
  };
}

function parseItem(raw: unknown): TimelineItem {
  const o = reqRecord(raw, 'timeline.item');
  const occurred_at = String(o.occurred_at ?? '');
  switch (o.kind) {
    case 'entry':
      return {
        kind: 'entry',
        occurred_at,
        entry: reqRecord(
          o.entry,
          'timeline.item.entry',
        ) as unknown as DialogueLog,
      };
    case 'contact_log':
      return {
        kind: 'contact_log',
        occurred_at,
        contact_log: parseContactLog(o.contact_log),
      };
    case 'deal_opened':
    case 'deal_status_changed':
    case 'deal_migrated':
      return {
        kind: o.kind,
        occurred_at,
        deal_event: parseDealEvent(o.deal_event),
      };
    case 'contract_concluded':
    case 'contract_renewed':
      return {
        kind: o.kind,
        occurred_at,
        contract_event: parseContractEvent(o.contract_event),
      };
    default:
      throw new ResponseMappingError(
        'timeline.item.kind',
        `unknown kind: ${typeof o.kind}`,
      );
  }
}

function parseContactLogTypeCounts(raw: unknown): ContactLogTypeCounts {
  const o = reqRecord(raw, 'timeline.counts.contact_log_types');
  return {
    inquiry: reqNumber(o.inquiry, 'timeline.counts.contact_log_types.inquiry'),
    seminar: reqNumber(o.seminar, 'timeline.counts.contact_log_types.seminar'),
    document_download: reqNumber(
      o.document_download,
      'timeline.counts.contact_log_types.document_download',
    ),
    useful_material: reqNumber(
      o.useful_material,
      'timeline.counts.contact_log_types.useful_material',
    ),
    in_person_event: reqNumber(
      o.in_person_event,
      'timeline.counts.contact_log_types.in_person_event',
    ),
    business_card_exchange: reqNumber(
      o.business_card_exchange,
      'timeline.counts.contact_log_types.business_card_exchange',
    ),
    community: reqNumber(
      o.community,
      'timeline.counts.contact_log_types.community',
    ),
    referral: reqNumber(
      o.referral,
      'timeline.counts.contact_log_types.referral',
    ),
    other: reqNumber(o.other, 'timeline.counts.contact_log_types.other'),
  };
}

function parseCounts(raw: unknown): TimelineCounts {
  const o = reqRecord(raw, 'timeline.counts');
  return {
    all: reqNumber(o.all, 'timeline.counts.all'),
    contact_log: reqNumber(o.contact_log, 'timeline.counts.contact_log'),
    dialog: reqNumber(o.dialog, 'timeline.counts.dialog'),
    sales_event: reqNumber(o.sales_event, 'timeline.counts.sales_event'),
    contact_log_types: parseContactLogTypeCounts(o.contact_log_types),
  };
}

export function parseTimeline(raw: unknown): TimelineResponse {
  const o = reqRecord(raw, 'timeline');
  const items = reqArray(o.items, 'timeline.items').map(parseItem);
  return {
    items,
    counts: o.counts === undefined ? null : parseCounts(o.counts),
    next_cursor: nullableString(o.next_cursor, 'timeline.next_cursor'),
  };
}
